import type { CropCalendarFormValues } from '../../../config/digisakaApi';

export type CropCalendarSampleKey = 'rice' | 'corn';

export type CropCalendarSampleResponse = {
  success: boolean;
  ndvi_success: boolean;
  llm_success: boolean;
  establishment_date: string;
  end_date: string;
  crop_variety: string;
  farm_id: string | null;
  sowing_date: string;
  planting_date: string;
  expected_harvest_date: string;
  planting_method: string;
  cropping_season: string;
  crop_status: string;
  cloud_filter: {
    applied: boolean;
    fraction_kept: number;
    points_before: number;
    points_after: number;
    method: string;
    note?: string;
  };
  ndvi_series: Array<{ date: string; ndvi: number; cloud_percentage?: number }>;
  ndvi_series_full: Array<{ date: string; ndvi: number; cloud_percentage?: number }>;
  ndvi_point_count: number;
  ndvi_point_count_full: number;
  llm_analysis: {
    current_growth_stage: { name: string; confidence: number; rationale: string };
    next_growth_stage_prediction: {
      name: string;
      estimated_days_min: number;
      estimated_days_max: number;
      notes: string;
    };
    growth_stage_periods: Array<{
      stage: string;
      tentative_start_date: string;
      tentative_end_date: string | null;
    }>;
    chart_series: Array<{ x: string; y: number; growth_stage: string; label: string | null }>;
    summary: string;
    predicted_harvest_date: string;
    caveats: string[];
  };
  farmer_readout: {
    summary: string;
    this_is_where_your_crop_is: string;
    in_plain_words: string;
    what_happens_next: string;
    about_when: string;
    tip: string;
    how_sure: string;
    predicted_harvest_date: string;
  };
  data: Array<{
    crop_establishment_date: string;
    planting_method: string;
    crop_variety: string;
    sowing_date: string;
    planting_date: string;
    expected_harvest_date: string;
    cropping_season: string;
  }>;
};

export type CropCalendarSample = {
  key: CropCalendarSampleKey;
  label: string;
  subtitle: string;
  icon: string;
  farmName: string;
  form: CropCalendarFormValues;
  response: CropCalendarSampleResponse;
};

function toIsoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(from: Date, days: number): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function buildNdviSeries(
  start: Date,
  end: Date,
  values: number[],
  intervalDays = 12
): Array<{ date: string; ndvi: number; cloud_percentage?: number }> {
  const rows: Array<{ date: string; ndvi: number; cloud_percentage?: number }> = [];
  let i = 0;
  for (let cursor = new Date(start); cursor <= end && i < values.length; cursor = addDays(cursor, intervalDays)) {
    rows.push({
      date: toIsoLocal(cursor),
      ndvi: values[i],
      cloud_percentage: [18, 42, 8, 55, 12, 28, 6, 35, 15, 22, 9, 48, 11, 20][i % 14],
    });
    i += 1;
  }
  return rows;
}

/** Sample calendars with dates anchored to today so early testers always see a realistic in-season report. */
export function buildCropCalendarSamples(referenceDate = new Date()): CropCalendarSample[] {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  const todayIso = toIsoLocal(today);

  const ricePlant = addDays(today, -82);
  const riceSow = addDays(ricePlant, -21);
  const riceHarvestPlan = addDays(today, 52);
  const riceHarvestPredict = addDays(today, 48);
  const ricePlantIso = toIsoLocal(ricePlant);
  const riceSowIso = toIsoLocal(riceSow);
  const riceHarvestPlanIso = toIsoLocal(riceHarvestPlan);
  const riceHarvestPredictIso = toIsoLocal(riceHarvestPredict);

  const riceNdvi = buildNdviSeries(
    ricePlant,
    today,
    [0.18, 0.22, 0.31, 0.42, 0.51, 0.58, 0.64, 0.71, 0.74, 0.76, 0.75, 0.73, 0.71, 0.69]
  );
  const cornPlant = addDays(today, -65);
  const cornSow = cornPlant;
  const cornHarvestPlan = addDays(today, 78);
  const cornHarvestPredict = addDays(today, 72);
  const cornPlantIso = toIsoLocal(cornPlant);
  const cornSowIso = toIsoLocal(cornSow);
  const cornHarvestPlanIso = toIsoLocal(cornHarvestPlan);
  const cornHarvestPredictIso = toIsoLocal(cornHarvestPredict);

  const cornNdvi = buildNdviSeries(
    cornPlant,
    today,
    [0.21, 0.28, 0.36, 0.44, 0.52, 0.58, 0.63, 0.66, 0.68, 0.67, 0.65, 0.64]
  );
  const riceChart = riceNdvi.slice(-6).map((p, idx) => ({
    x: p.date,
    y: p.ndvi,
    growth_stage: ['Tillering', 'Stem elongation', 'Panicle initiation', 'Flowering', 'Grain filling', 'Grain filling'][idx] || 'Grain filling',
    label: null,
  }));

  const cornChart = cornNdvi.slice(-6).map((p, idx) => ({
    x: p.date,
    y: p.ndvi,
    growth_stage: ['Vegetative', 'Vegetative', 'Late vegetative', 'Tasseling', 'Silking', 'Grain fill start'][idx] || 'Vegetative',
    label: null,
  }));

  return [
    {
      key: 'rice',
      label: 'Rice · Wet season',
      subtitle: 'Transplanted · Isabela demo field',
      icon: 'ri-plant-line',
      farmName: 'San Mateo Demo Rice Field',
      form: {
        crop_type: 'Rice',
        planting_method: 'Transplanted',
        sowing_date: riceSowIso,
        planting_date: ricePlantIso,
        expected_harvest_date: riceHarvestPlanIso,
        cropping_season: 'Wet',
      },
      response: {
        success: true,
        ndvi_success: true,
        llm_success: true,
        establishment_date: ricePlantIso,
        end_date: todayIso,
        crop_variety: 'Rice',
        farm_id: 'sample-rice',
        sowing_date: riceSowIso,
        planting_date: ricePlantIso,
        expected_harvest_date: riceHarvestPlanIso,
        planting_method: 'Transplanted',
        cropping_season: 'Wet',
        crop_status: 'Growing',
        cloud_filter: {
          applied: true,
          fraction_kept: 0.45,
          points_before: 28,
          points_after: riceNdvi.length,
          method: 'clearest_fraction',
          note: 'Sample data for demo purposes.',
        },
        ndvi_series: riceNdvi,
        ndvi_series_full: riceNdvi,
        ndvi_point_count: riceNdvi.length,
        ndvi_point_count_full: riceNdvi.length,
        llm_analysis: {
          current_growth_stage: {
            name: 'Grain filling',
            confidence: 0.82,
            rationale:
              'Greenness rose steadily after transplanting and has levelled off at a strong level — typical once grains are filling.',
          },
          next_growth_stage_prediction: {
            name: 'Maturity / harvest readiness',
            estimated_days_min: 42,
            estimated_days_max: 55,
            notes: 'Watch for golden panicles and lower field moisture before cutting.',
          },
          growth_stage_periods: [
            { stage: 'Land preparation', tentative_start_date: toIsoLocal(addDays(ricePlant, -28)), tentative_end_date: toIsoLocal(addDays(ricePlant, -7)) },
            { stage: 'Transplanting', tentative_start_date: toIsoLocal(addDays(ricePlant, -6)), tentative_end_date: ricePlantIso },
            { stage: 'Tillering', tentative_start_date: toIsoLocal(addDays(ricePlant, 7)), tentative_end_date: toIsoLocal(addDays(ricePlant, 28)) },
            { stage: 'Stem elongation', tentative_start_date: toIsoLocal(addDays(ricePlant, 29)), tentative_end_date: toIsoLocal(addDays(ricePlant, 45)) },
            { stage: 'Panicle initiation', tentative_start_date: toIsoLocal(addDays(ricePlant, 46)), tentative_end_date: toIsoLocal(addDays(ricePlant, 58)) },
            { stage: 'Flowering', tentative_start_date: toIsoLocal(addDays(ricePlant, 59)), tentative_end_date: toIsoLocal(addDays(ricePlant, 68)) },
            { stage: 'Grain filling', tentative_start_date: toIsoLocal(addDays(ricePlant, 69)), tentative_end_date: riceHarvestPredictIso },
            { stage: 'Harvest', tentative_start_date: riceHarvestPredictIso, tentative_end_date: null },
          ],
          chart_series: riceChart,
          summary:
            'Your wet-season rice looks healthy and is in grain filling. Greenness built up well after transplanting and is holding strong.',
          predicted_harvest_date: riceHarvestPredictIso,
          caveats: ['Sample data — not tied to a live satellite pass.'],
        },
        farmer_readout: {
          summary:
            'Your wet-season rice looks healthy and is in grain filling. Greenness built up well after transplanting and is holding strong.',
          this_is_where_your_crop_is: 'Grain filling',
          in_plain_words:
            'The field stayed green and kept building cover after transplanting. That pattern matches a crop that moved through tillering and flowering and is now filling grain.',
          what_happens_next: 'Maturity and harvest readiness',
          about_when: `Roughly ${Math.round((riceHarvestPredict.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))}–${Math.round((addDays(riceHarvestPredict, 10).getTime() - today.getTime()) / (1000 * 60 * 60 * 24))} days from now, around late November if weather stays normal.`,
          tip: 'Scout for golden panicles and plan harvest when grain moisture is right — avoid rushing while the field is still very wet.',
          how_sure: 'Fairly confident — clear satellite views and a steady greenness curve',
          predicted_harvest_date: riceHarvestPredictIso,
        },
        data: [
          {
            crop_establishment_date: ricePlantIso,
            planting_method: 'Transplanted',
            crop_variety: 'Rice',
            sowing_date: riceSowIso,
            planting_date: ricePlantIso,
            expected_harvest_date: riceHarvestPlanIso,
            cropping_season: 'Wet',
          },
        ],
      },
    },
    {
      key: 'corn',
      label: 'Corn · Dry season',
      subtitle: 'Direct seeded · Nueva Ecija demo field',
      icon: 'ri-seedling-line',
      farmName: 'Cabiao Demo Corn Field',
      form: {
        crop_type: 'Corn',
        planting_method: 'Direct seeded',
        sowing_date: cornSowIso,
        planting_date: '',
        expected_harvest_date: cornHarvestPlanIso,
        cropping_season: 'Dry',
      },
      response: {
        success: true,
        ndvi_success: true,
        llm_success: true,
        establishment_date: cornSowIso,
        end_date: todayIso,
        crop_variety: 'Corn',
        farm_id: 'sample-corn',
        sowing_date: cornSowIso,
        planting_date: '',
        expected_harvest_date: cornHarvestPlanIso,
        planting_method: 'Direct seeded',
        cropping_season: 'Dry',
        crop_status: 'Growing',
        cloud_filter: {
          applied: true,
          fraction_kept: 0.45,
          points_before: 22,
          points_after: cornNdvi.length,
          method: 'clearest_fraction',
          note: 'Sample data for demo purposes.',
        },
        ndvi_series: cornNdvi,
        ndvi_series_full: cornNdvi,
        ndvi_point_count: cornNdvi.length,
        ndvi_point_count_full: cornNdvi.length,
        llm_analysis: {
          current_growth_stage: {
            name: 'Silking / early grain fill',
            confidence: 0.78,
            rationale:
              'Greenness climbed through vegetative growth and is now near its seasonal peak — common as tassels and silks appear.',
          },
          next_growth_stage_prediction: {
            name: 'Grain fill to maturity',
            estimated_days_min: 65,
            estimated_days_max: 80,
            notes: 'Kernel fill depends on rainfall or irrigation through the next month.',
          },
          growth_stage_periods: [
            { stage: 'Emergence', tentative_start_date: cornSowIso, tentative_end_date: toIsoLocal(addDays(cornPlant, 12)) },
            { stage: 'Vegetative', tentative_start_date: toIsoLocal(addDays(cornPlant, 13)), tentative_end_date: toIsoLocal(addDays(cornPlant, 38)) },
            { stage: 'Tasseling', tentative_start_date: toIsoLocal(addDays(cornPlant, 39)), tentative_end_date: toIsoLocal(addDays(cornPlant, 48)) },
            { stage: 'Silking', tentative_start_date: toIsoLocal(addDays(cornPlant, 49)), tentative_end_date: toIsoLocal(addDays(cornPlant, 58)) },
            { stage: 'Grain fill', tentative_start_date: toIsoLocal(addDays(cornPlant, 59)), tentative_end_date: cornHarvestPredictIso },
            { stage: 'Maturity', tentative_start_date: cornHarvestPredictIso, tentative_end_date: null },
          ],
          chart_series: cornChart,
          summary:
            'Your direct-seeded corn is moving into silking with strong green cover. The field built biomass steadily since emergence.',
          predicted_harvest_date: cornHarvestPredictIso,
          caveats: ['Sample data — not tied to a live satellite pass.'],
        },
        farmer_readout: {
          summary:
            'Your direct-seeded corn is moving into silking with strong green cover. The field built biomass steadily since emergence.',
          this_is_where_your_crop_is: 'Silking / early grain fill',
          in_plain_words:
            'Greenness kept climbing week after week — a good sign the stand is thick and the crop is using the season well.',
          what_happens_next: 'Grain fill through to maturity',
          about_when: `Harvest may land around ${Math.round((cornHarvestPredict.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))} days from now if moisture and heat stay favourable.`,
          tip: 'Keep an eye on water stress during silking — even a short dry spell can hurt kernel set.',
          how_sure: 'Moderately confident — good recent views with a consistent upward trend',
          predicted_harvest_date: cornHarvestPredictIso,
        },
        data: [
          {
            crop_establishment_date: cornSowIso,
            planting_method: 'Direct seeded',
            crop_variety: 'Corn',
            sowing_date: cornSowIso,
            planting_date: '',
            expected_harvest_date: cornHarvestPlanIso,
            cropping_season: 'Dry',
          },
        ],
      },
    },
  ];
}

export const CROP_CALENDAR_SAMPLES = buildCropCalendarSamples();

export function getCropCalendarSample(key: CropCalendarSampleKey): CropCalendarSample {
  return buildCropCalendarSamples().find((s) => s.key === key) ?? buildCropCalendarSamples()[0];
}
